import { useContext } from "react";

import { UserContext } from "@/app/providers";
import { SATOSPortal } from "@/data-adapter/SATOSPortal";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { USE_FETCH_SEARCH_CONFIGURATION } from "@/model/Application/SearchResult";
import { ApplicationConfiguration } from "@/model/ApplicationConfiguration";
import { PermissionLevelEnum } from "@/model/Permission/PermissionLevel";
import {
	User,
	UserCompaniesResponse,
	UserGroup,
	UserSummaryResponse,
} from "@/model/User/User";
import { UserCompanyConfiguration } from "@/model/user-company-configuration/UserCompanyConfiguration";

import { CompanyQueryKeys } from "@/service/CompanyQueries";

dayjs.extend(utc);

export const checkAccess = (minAccess: PermissionLevelEnum, user: User) => {
	return (
		user &&
		user.IsAuthenticated &&
		user.PermissionLevel &&
		user.PermissionLevel.Level >= (minAccess as number)
	);
};

export const checkGroupAccess = (
	groups: Array<UserGroup>,
	acceptedGroupIds: Array<number> | undefined,
) => {
	if (!acceptedGroupIds || acceptedGroupIds.length === 0) {
		return true;
	}

	return groups
		.filter((group) => group.Access >= 1 || group.Status >= 1)
		.map((group) => group.Id)
		.some((id) => acceptedGroupIds.includes(id));
};

export const checkIfAtLeastGuestAccess = (user: User) => {
	return checkAccess(PermissionLevelEnum.Guest, user);
};

export const checkAdminAccess = (user: User) => {
	return checkAccess(PermissionLevelEnum.Administrator, user);
};

export const checkServerAdminAccess = (user: User) => {
	return checkAccess(PermissionLevelEnum.ServerAdministrator, user);
};

export const useFetchActiveUser = () => {
	return useQuery<User>({
		queryKey: [ApplicationQueryKeys.user],
		queryFn: () => SATOSPortal.GetCurrentUser(),
		staleTime: 5000,
	});
};

export const useFetchApplicationConfiguration = () => {
	return useQuery<ApplicationConfiguration>({
		queryKey: [ApplicationQueryKeys.appConfiguration],
		queryFn: () => SATOSPortal.GetApplicationConfiguration(),
		staleTime: 30000,
	});
};

export const useFetchActiveUserCompanyConfiguration = () => {
	return useQuery<UserCompanyConfiguration>({
		queryKey: [
			ApplicationQueryKeys.user,
			ApplicationQueryKeys.userCompanyConfiguration,
		],
		queryFn: () => SATOSPortal.UserCompanyConfiguration(),
		staleTime: 5000,
	});
};

export const useFetchUserCompanies = () => {
	return useQuery<UserCompaniesResponse>({
		queryKey: [ApplicationQueryKeys.user, ApplicationQueryKeys.companies],
		queryFn: () => SATOSPortal.GetUserCompanies(),
	});
};

export const useFetchUsers = (companyId?: number) => {
	return useQuery<UserSummaryResponse[]>({
		queryKey: [CompanyQueryKeys.companies, companyId, "users"],
		queryFn: () => SATOSPortal.GetUsers(companyId),
	});
};

export const useFetchUserSingle = (userId: number) => {
	return useQuery({
		queryKey: [ApplicationQueryKeys.user, userId],
		queryFn: () => SATOSPortal.GetUser(userId),
	});
};

export const useFetchSearch = (searchTerm: string) => {
	const user = useContext(UserContext);
	return useQuery({
		queryKey: [ApplicationQueryKeys.search, user?.CompanyId, searchTerm],
		queryFn: () => SATOSPortal.Search(searchTerm),
		enabled:
			!!searchTerm &&
			searchTerm.length >=
				USE_FETCH_SEARCH_CONFIGURATION.minimumSearchTermLength,
	});
};

export const enrichUser = (user: User | null): User | null => {
	const initials = getUserInitials(user);

	if (!user) {
		return null;
	}

	return { ...user, Initials: initials };
};

const getUserInitials = (user: User | null) => {
	if (!user) return "";
	const name = user.Name;
	const split = name.split(/[-_\s]/); // Split on "_", "-" and " "

	let initials1 = split[0];
	if (initials1.length > 0) initials1 = initials1[0];
	let initials2 = "";
	if (split.length > 1) initials2 = split[split.length - 1];

	if (initials2.length > 0) initials2 = initials2[0];

	return initials1.toLocaleUpperCase() + " " + initials2.toLocaleUpperCase();
};

export const insertDomain = (
	value: string,
	options: { insertIntoHtmlElement: boolean; markdown?: boolean } = {
		insertIntoHtmlElement: true,
		markdown: false,
	},
): string => {
	//https://stackoverflow.com/a/57949518/1163614
	const isLocalhost = Boolean(
		window.location.hostname === "localhost" ||
		// [::1] is the IPv6 localhost address.
		window.location.hostname === "[::1]" ||
		// 127.0.0.1/8 is considered localhost for IPv4.
		window.location.hostname.match(
			/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
		),
	);

	const emptyString = "";

	const shouldNotReplaceValue =
		(options.insertIntoHtmlElement && !isLocalhost) ||
		(!options.insertIntoHtmlElement && !options.markdown && isLocalhost);

	if (shouldNotReplaceValue) {
		return value ?? emptyString;
	}

	const parseText = (
		input: string,
		insertIntoHtmlElement: boolean | undefined,
	) => {
		if (insertIntoHtmlElement) {
			return input; //htmlElementParse(markdownImageParse(input));
		} else {
			return urlParse(input); //markdownImageParse(input));
		}
	};

	const urlParse = (input: string): string => {
		/* 
      /(http|https)(:\/\/)(.*:\d*\/)(.+)/
        1st Capturing Group (http|https)
          1st Alternative http
          http matches the characters http literally (case sensitive)
          2nd Alternative https
          https matches the characters https literally (case sensitive)
        2nd Capturing Group (:\/\/)
          : matches the character : with index 5810 (3A16 or 728) literally (case sensitive)
          \/ matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
          \/ matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
        3rd Capturing Group (.*:\d*\/)
          . matches any character (except for line terminators)
          * matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
          : matches the character : with index 5810 (3A16 or 728) literally (case sensitive)
          \d matches a digit (equivalent to [0-9])
          * matches the previous token between zero and unlimited times, as many times as possible, giving back as needed (greedy)
          \/ matches the character / with index 4710 (2F16 or 578) literally (case sensitive)
        4th Capturing Group (.+)
          . matches any character (except for line terminators)
          + matches the previous token between one and unlimited times, as many times as possible, giving back as needed (greedy) 
    */
		return !input
			? emptyString
			: input.replace(
					/(http|https)(:\/\/)(.*:\d*\/)(.+)/,
					`$1://${window.location.hostname}/$4`,
				);
	};

	return parseText(value, options.insertIntoHtmlElement);
};

export const initialDate = (offset: number | undefined = undefined) => {
	if (offset === undefined) {
		return dayjs().startOf("day").toDate();
	}
	const date = dayjs().startOf("day").toDate();
	date.setDate(date.getDate() + offset);

	return date;
};

export const getFirstDayOfYear = () => {
	return dayjs().startOf("year").startOf("day").toDate();
};

export class ApplicationQueryKeys {
	static readonly search = "search";
	static readonly user = "user";
	static readonly userCompanyConfiguration = "usercompanyconfiguration";
	static readonly serverResponding = "server-responding";
	static readonly companies = "companies";
	static readonly language = "language";
	static readonly shiftSettings = "shiftSettings";
	static readonly appConfiguration = "app configuration";
}
